import { RefObject, useEffect, useRef, useState } from 'react';

import { isBrowser } from 'src/utils/UserAgent';

export type ResizeObserverTargetSize = Omit<DOMRect, 'toJSON'>;

export const initialResizeObserverTargetSize: ResizeObserverTargetSize = {
  width: 0,
  height: 0,
  left: 0,
  top: 0,
  bottom: 0,
  right: 0,
  x: 0,
  y: 0,
};

/**
 * A hook for managing window size events
 * @param {RefObject<HTMLElement>} targetRef A target to observe the element size
 * @param {Function} callback A function to handle the new window height, rather than relying on a state change. This helps to avoid render each time
 * @example
 * const { width, height } = useResizeObserver();
 * @example
 * useResizeObserver({ callback: ({ width, height }) => {} });
 */
export function useResizeObserver({
  targetRef,
  callback,
}: {
  targetRef: RefObject<HTMLElement>;
  callback?: (size: ResizeObserverTargetSize) => void;
}): ResizeObserverTargetSize {
  const targetSizeRef = useRef(initialResizeObserverTargetSize);
  const [elementSize, setResizeObserver] = useState(targetSizeRef.current);

  const update = (nextSize: DOMRect) => {
    targetSizeRef.current = nextSize;

    if (callback) {
      callback(nextSize);
    } else {
      setResizeObserver(nextSize);
    }
  };

  useEffect(() => {
    if (!isBrowser || !targetRef.current) return;

    let resizeObserver: ResizeObserver | undefined;
    const hasResizeObserver = 'ResizeObserver' in window;

    if (!hasResizeObserver) return;

    resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        update(entry.contentRect);
      }
    });

    resizeObserver.observe(targetRef.current);

    return () => {
      if (hasResizeObserver && resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return elementSize;
}
